import * as React from 'react'
import { bind } from 'classnames/bind'
import * as styles from '@procore/core-css/src/components/_button.scss'

import { ButtonAttributes, Props, SizeNS } from '../utils/types'
import Icon from './Icon'
import Spinner, { SpinnerVariantNS } from './Spinner'

type ButtonSize = typeof SizeNS.sm | typeof SizeNS.md | typeof SizeNS.lg

export namespace ButtonTypeNS {
  export const button = 'button'
  export const reset = 'reset'
  export const submit = 'submit'
}

export namespace ButtonModifierNS {
  export const block = 'block'
  export const error = 'error'
  export const iconOnly = 'icon-only'
  export const loading = 'loading'
  export const open = 'open'
  export const withArrow = 'with-arrow'
}

export namespace ButtonVariantNS {
  export const actionOutline = 'action-outline'
  export const dropdown = 'dropdown'
  export const dropdownSecondary = 'dropdown-secondary'
  export const errorOutline = 'error-outline'
  export const form = 'form'
  export const infoOutline = 'info-outline'
  export const primary = 'primary'
  export const secondary = 'secondary'
  export const successOutline = 'success-outline'
  export const tertiary = 'tertiary'
}

type ButtonType =
  | typeof ButtonTypeNS.button
  | typeof ButtonTypeNS.reset
  | typeof ButtonTypeNS.submit

export type ButtonModifier =
  | typeof ButtonModifierNS.block
  | typeof ButtonModifierNS.error
  | typeof ButtonModifierNS.iconOnly
  | typeof ButtonModifierNS.loading
  | typeof ButtonModifierNS.open
  | typeof ButtonModifierNS.withArrow

export type ButtonVariant =
  | typeof ButtonVariantNS.actionOutline
  | typeof ButtonVariantNS.dropdown
  | typeof ButtonVariantNS.dropdownSecondary
  | typeof ButtonVariantNS.errorOutline
  | typeof ButtonVariantNS.form
  | typeof ButtonVariantNS.infoOutline
  | typeof ButtonVariantNS.primary
  | typeof ButtonVariantNS.secondary
  | typeof ButtonVariantNS.successOutline
  | typeof ButtonVariantNS.tertiary

export interface ButtonProps extends ButtonAttributes, Props {
  block?: boolean
  className?: string
  disabled?: boolean
  dropdown?: boolean
  error?: boolean
  icon?: string
  loading?: boolean
  open?: boolean
  size?: ButtonSize
  type?: ButtonType
  variant?: ButtonVariant
}

const cx = bind(styles)

const getSpinnerVariant = (variant: ButtonVariant) =>
  [ButtonVariantNS.form, ButtonVariantNS.primary].indexOf(variant) >= 0
    ? SpinnerVariantNS.light
    : SpinnerVariantNS.none

const getSpinnerSize = (size: ButtonSize, dropdown: Boolean) =>
  size === SizeNS.sm || dropdown ? SizeNS.xs : SizeNS.sm

const getLabel = (child: any) =>
  child.type === Icon ? (
    <div className={cx('core-button__label', 'core-button__label--icon')}>
      {React.cloneElement(child, { size: SizeNS.sm })}
    </div>
  ) : (
    <div className={cx('core-button__label')}>{child}</div>
  )

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      block = false,
      children,
      className,
      disabled = false,
      dropdown = false,
      error = false,
      icon,
      loading = false,
      open = false,
      size = SizeNS.md,
      type = ButtonTypeNS.button,
      variant = ButtonVariantNS.primary,
      ...props
    },
    ref
  ) => (
    <button
      ref={ref}
      type={type}
      className={cx(
        'core-button',
        `core-button--${size}`,
        {
          [`core-button--${ButtonModifierNS.block}`]: block,
          [`core-button--${ButtonModifierNS.error}`]: error,
          [`core-button--${ButtonModifierNS.iconOnly}`]: !children && icon,
          [`core-button--${ButtonModifierNS.loading}`]: loading,
          [`core-button--${ButtonModifierNS.open}`]: open,
          [`core-button--${ButtonModifierNS.withArrow}`]: dropdown,
          [`core-button--${variant}`]: variant,
        },
        className
      )}
      disabled={disabled || loading}
      {...props}
    >
      {icon && getLabel(<Icon icon={icon} />)}
      {React.Children.toArray(children)
        .filter(child => child)
        .map(getLabel)
        .map((label, key) => React.cloneElement(label, { key }))}
      <div className={cx('core-button__spinner')}>
        <Spinner
          size={getSpinnerSize(size, dropdown)}
          variant={getSpinnerVariant(variant)}
        />
      </div>
    </button>
  )
)

Button.displayName = 'Button'

export default Button
